import { useState } from "react";
import { ChevronDown, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePhoneSubscription } from "@/hooks/billing/usePhoneSubscription";
import { usePhoneNumber } from "@/hooks/phone";
import { GeniePhoneNumber, PhoneBillingStatus, PhoneSubscription } from "@/services/billing/SubscriptionService";

// A number added while the account had no charge stays free, so the account owner only ever sees "No charge (trial)".
const BILLING_LABELS: Record<PhoneBillingStatus, { label: string; className: string }> = {
  billed: { label: "Billed", className: "bg-genie-primary/10 text-genie-primary" },
  no_charge_admin: { label: "No charge (admin)", className: "bg-gray-100 text-gray-600" },
  no_charge_trial: { label: "No charge (trial)", className: "bg-gray-100 text-gray-600" },
  unbilled: { label: "No charge (trial)", className: "bg-gray-100 text-gray-600" },
};

export const ActivePhoneNumbersSection = () => {
  const { phoneSubscriptions, geniePhoneNumbers, isLoadingSubscriptions, removePhoneNumber, refetchSubscriptions } =
    usePhoneSubscription();
  const { release, isReleasing } = usePhoneNumber();
  const [releasingPhoneId, setReleasingPhoneId] = useState<string | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);

  const cancelledPhones = phoneSubscriptions.filter((phone) => phone.status === "cancelled");

  const handleRelease = async (phone: GeniePhoneNumber) => {
    if (!confirm(`Are you sure you want to release this phone number? This action cannot be undone.\n\n${phone.phone_number}`)) {
      return;
    }

    try {
      setReleasingPhoneId(phone.phone_number_id);
      await release(phone.phone_number_id);
      if (phone.billing === "billed") {
        await removePhoneNumber(phone.phone_number);
      } else {
        await refetchSubscriptions();
      }
      toast.success(`Phone number ${phone.phone_number} released successfully`);
    } catch (error) {
      console.error("Error releasing phone number:", error);
      toast.error("Failed to release phone number");
    } finally {
      setReleasingPhoneId(null);
    }
  };

  const GeniePhoneItem = ({ phone }: { phone: GeniePhoneNumber }) => {
    const billing = BILLING_LABELS[phone.billing];
    const isReleasingThis = releasingPhoneId === phone.phone_number_id;
    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex-1 min-w-0">
          {phone.agent_name && <div className="text-genie-primary font-medium truncate">{phone.agent_name}</div>}
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">{phone.phone_number}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${billing.className}`}>
              {billing.label}
            </span>
          </div>
        </div>
        <button
          onClick={() => handleRelease(phone)}
          disabled={isReleasing || isReleasingThis}
          className="flex items-center justify-center min-h-[44px] px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm disabled:opacity-50 self-start sm:self-auto"
        >
          {isReleasingThis ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
              Releasing...
            </>
          ) : (
            <>
              <Trash2 className="h-4 w-4 mr-2" />
              Release
            </>
          )}
        </button>
      </div>
    );
  };

  const CancelledPhoneItem = ({ phone }: { phone: PhoneSubscription }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex-1">
        {phone.agents?.name && <div className="text-genie-primary font-medium">{phone.agents.name}</div>}
        <div className="font-medium text-gray-900">{phone.phone_number}</div>
        <div className="text-xs text-gray-500 capitalize">Type: {phone.phone_type}</div>
      </div>
    </div>
  );

  if (isLoadingSubscriptions) {
    return (
      <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center mb-[18px]">
          <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
            <Phone className="h-5 w-5 text-genie-primary" />
          </div>
          <h2 className="text-xl font-semibold">Active Phone Numbers</h2>
        </div>
        <div className="text-center text-gray-500">Loading phone numbers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center mb-[18px]">
          <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
            <Phone className="h-5 w-5 text-genie-primary" />
          </div>
          <h2 className="text-xl font-semibold">Active Phone Numbers</h2>
        </div>

        {geniePhoneNumbers.length === 0 ? (
          <div className="text-center text-gray-500 py-4">No active phone numbers. Provision one in your agent settings.</div>
        ) : (
          <div className="space-y-3">
            {geniePhoneNumbers.map((phone) => (
              <GeniePhoneItem key={phone.id} phone={phone} />
            ))}
          </div>
        )}
      </div>

      {cancelledPhones.length > 0 && (
        <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
          <div className="rounded-[10px] bg-white overflow-hidden">
            <button
              onClick={() => setShowCancelled(!showCancelled)}
              className="w-full px-5 py-4 flex items-center justify-between bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
            >
              <span className="font-medium text-gray-900">Cancelled Phone Numbers ({cancelledPhones.length})</span>
              <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${showCancelled ? "rotate-180" : ""}`} />
            </button>
            {showCancelled && (
              <div className="px-5 py-4 border-t space-y-3">
                {cancelledPhones.map((phone) => (
                  <CancelledPhoneItem key={phone.id} phone={phone} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
